import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, TextField, Button, IconButton, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Chip, InputBase,
} from '@mui/material';
import {
    RemoveCircleOutline, AddCircleOutline, Close,
    Search as SearchIcon, Clear as ClearIcon,
} from '@mui/icons-material';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { productCategoryList } from '../utils/groupByCategory';
import { TOUCH } from '../theme/tokens';
import { alpha } from '@mui/material/styles';
import { formatINR } from './common/Money';
import ScrollChipRow from './common/ScrollChipRow';

const rupees = (n) => formatINR(n, { decimals: Number.isInteger(Math.round((Number(n) || 0) * 100) / 100) ? 0 : 2 });

// A product's rate on the day being worked: that weekday's price if it has one, else the base.
// day_price_overrides is keyed 0=Mon..6=Sun, the server's weekday numbering.
const rateOn = (p, isoDate) => {
    const js = new Date(`${isoDate}T00:00:00`).getDay();          // 0=Sun..6=Sat
    const override = p.day_price_overrides?.[String((js + 6) % 7)];
    return parseFloat(override ?? p.base_price ?? p.current_price ?? 0) || 0;
};

// Same heading the Daily Ops summary uses for products with no category.
const OTHER_CATEGORY = 'No category';

// Today's quantity for one of the customer's lines, plus what it is built from.
// base = what the plan delivers without any change today (0 for a paused or one-off line).
export const lineOf = (sub) => {
    const txns = sub.recorded_txns || [];
    const isAbsent = txns.some(t => t.origin === 'Absent');
    const base = sub.is_suspended ? 0 : (sub.subscribed_qty || 0);
    const extra = txns.filter(t => t.origin === 'Extra').reduce((n, t) => n + (t.qty || 0), 0);
    const less = txns.filter(t => t.origin === 'Less').reduce((n, t) => n + Math.abs(t.qty || 0), 0);
    const adjustTxnIds = txns.filter(t => t.origin === 'Extra' || t.origin === 'Less').map(t => t.txn_id);
    return { base, current: isAbsent ? 0 : base + extra - less, isAbsent, adjustTxnIds };
};

/**
 * Pick what a customer gets today, laid out like the customer order page: their plan items
 * first with a quantity each, then every other product by category with an Add button.
 * A dropdown of 20-30 products was too hard to scroll and pick from on a phone.
 *
 * onSave receives one change per product whose quantity moved:
 *   { productId, base, target, undoTxnIds }
 * The page turns each into "clear the old Extra/Less entries, then record one entry for the
 * difference from the plan", so the card never stacks up "+1 extra, -1 less" on one line.
 */
export default function DailyOpsProductPicker({ customer, products, categoryOrder, date, onClose, onSave }) {
    const [qty, setQty] = useState({});       // productId -> chosen quantity (only touched ones)
    const [search, setSearch] = useState('');
    const [catFilter, setCatFilter] = useState('');   // '' = all categories
    // What is being typed into a quantity box, while it is being typed: an empty box or "2."
    // is a normal moment mid-typing, not a quantity, so it must not snap back to a number.
    const [draft, setDraft] = useState({});           // productId -> text
    const [saving, setSaving] = useState(false);

    useEffect(() => { setQty({}); setSearch(''); setCatFilter(''); setDraft({}); setSaving(false); }, [customer]);

    // The customer's own lines: plan items (incl. paused) and anything already added today.
    // Monthly flat fee lines have no daily quantity, so there is nothing to change here.
    const planLines = useMemo(() => (customer?.subscriptions || [])
        .filter(s => s.billing_type !== 'MonthlyFlatFee')
        .map(s => ({ sub: s, ...lineOf(s) })), [customer]);

    // Everything else, grouped by the product's first category in the vendor's category
    // order. Products already on the customer's card are left out — they are above. So are
    // monthly flat fee products: they are billed by the month, not delivered by the day, and
    // adding one here would charge a whole month's fee as a one-day extra.
    const otherGroups = useMemo(() => {
        const taken = new Set((customer?.subscriptions || []).map(s => s.product_id));
        const list = (products || [])
            .filter(p => !taken.has(p.f_id) && p.f_billing_type !== 'MonthlyFlatFee')
            .sort((a, b) => (a.f_sort_order || 0) - (b.f_sort_order || 0)
                || (a.f_name || '').localeCompare(b.f_name || ''));
        const grouped = {};
        const appearance = [];
        for (const p of list) {
            const cat = productCategoryList(p)[0] || OTHER_CATEGORY;
            if (!grouped[cat]) { grouped[cat] = []; appearance.push(cat); }
            grouped[cat].push(p);
        }
        const order = [
            ...(categoryOrder || []).filter(c => grouped[c] && c !== OTHER_CATEGORY),
            ...appearance.filter(c => c !== OTHER_CATEGORY && !(categoryOrder || []).includes(c)),
            ...(grouped[OTHER_CATEGORY] ? [OTHER_CATEGORY] : []),
        ];
        return order.map(cat => ({ cat, items: grouped[cat] }));
    }, [customer, products, categoryOrder]);

    const q = search.trim().toLowerCase();
    const matches = (name) => !q || (name || '').toLowerCase().includes(q);
    // A category chip narrows the list to that category, so the customer's own items step
    // aside while one is picked — they are not "in" any one category list.
    const shownPlan = catFilter ? [] : planLines.filter(l => matches(l.sub.product_name));
    const shownGroups = otherGroups
        .filter(g => !catFilter || g.cat === catFilter)
        .map(g => ({ ...g, items: g.items.filter(p => matches(p.f_name)) }))
        .filter(g => g.items.length);

    const valueOf = (id, current) => (qty[id] ?? current);
    const setTo = (id, current, value) => setQty(prev => {
        const next = Math.max(0, Math.round(value * 1000) / 1000);
        const copy = { ...prev };
        if (next === current) delete copy[id]; else copy[id] = next;
        return copy;
    });
    const change = (id, current, delta) => {
        setDraft(d => { const c = { ...d }; delete c[id]; return c; });
        setTo(id, current, valueOf(id, current) + delta);
    };
    // Typing a quantity: for vendors who sell in big numbers, tapping + twenty-five times is
    // not an option. Accepts decimals (2.5 kg); anything else in the box is ignored.
    const typeQty = (id, current, text) => {
        if (!/^\d*\.?\d{0,3}$/.test(text)) return;
        setDraft(d => ({ ...d, [id]: text }));
        const n = parseFloat(text);
        setTo(id, current, Number.isFinite(n) ? n : 0);
    };
    const endTyping = (id) => setDraft(d => { const c = { ...d }; delete c[id]; return c; });

    const changes = [
        ...planLines
            .filter(l => !l.isAbsent && qty[l.sub.product_id] !== undefined)
            .map(l => ({ productId: l.sub.product_id, base: l.base,
                         target: qty[l.sub.product_id], undoTxnIds: l.adjustTxnIds })),
        ...otherGroups.flatMap(g => g.items)
            .filter(p => qty[p.f_id] !== undefined)
            .map(p => ({ productId: p.f_id, base: 0, target: qty[p.f_id], undoTxnIds: [] })),
    ];

    // What this customer comes to for the day, with the quantities as they stand in the picker.
    const total = planLines.reduce((n, l) => n + (l.isAbsent ? 0 : valueOf(l.sub.product_id, l.current) * (l.sub.price_per_unit || 0)), 0)
        + otherGroups.flatMap(g => g.items).reduce((n, p) => n + (qty[p.f_id] || 0) * rateOn(p, date), 0);

    // "₹40 × 3 = ₹120", or just the rate while nothing is chosen.
    const priceLine = (rate, v) => (rate > 0
        ? (v > 0 ? <>{rupees(rate)} × {v} = <b>{rupees(rate * v)}</b></> : rupees(rate))
        : null);

    const handleSave = async () => {
        if (!changes.length) { onClose(); return; }
        setSaving(true);
        try {
            await onSave(changes);
        } finally {
            setSaving(false);
        }
    };

    const stepper = (id, current, { disabled = false, showAdd = false } = {}) => {
        const v = valueOf(id, current);
        // Not while typing: clearing the box to type "25" briefly makes it 0.
        if (showAdd && v === 0 && draft[id] === undefined) {
            return (
                <Button variant="outlined" size="small" disabled={disabled}
                    onClick={() => change(id, current, 1)}
                    sx={{ minWidth: 64, height: TOUCH.dense, fontWeight: 700, borderRadius: 1.5, textTransform: 'none' }}>
                    Add
                </Button>
            );
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton disabled={disabled || v <= 0} onClick={() => change(id, current, -1)}
                    sx={{ width: TOUCH.min, height: TOUCH.min }} aria-label="Less">
                    <RemoveCircleOutline color={disabled || v <= 0 ? 'disabled' : 'primary'} />
                </IconButton>
                {/* Tap the number to type it — the keypad opens straight to digits */}
                <InputBase
                    value={draft[id] ?? String(v)}
                    onChange={e => typeQty(id, current, e.target.value)}
                    onFocus={e => e.target.select()}
                    onBlur={() => endTyping(id)}
                    disabled={disabled}
                    inputProps={{
                        inputMode: 'decimal', 'aria-label': 'Quantity',
                        style: { textAlign: 'center', fontWeight: 700, padding: '6px 0' },
                    }}
                    sx={{
                        width: 52, height: 36, fontSize: '1rem',
                        border: 1, borderColor: 'divider', borderRadius: 1,
                        '&.Mui-focused': { borderColor: 'primary.main' },
                    }}
                />
                <IconButton disabled={disabled} onClick={() => change(id, current, 1)}
                    sx={{ width: TOUCH.min, height: TOUCH.min }} aria-label="More">
                    <AddCircleOutline color={disabled ? 'disabled' : 'primary'} />
                </IconButton>
            </Box>
        );
    };

    const rowSx = {
        display: 'flex', alignItems: 'center', gap: 1, minHeight: TOUCH.min,
        py: 0.25, pl: 1.5, borderBottom: 1, borderColor: 'divider',
    };
    // Same band as the Daily Ops summary headings, so a heading never reads as a product.
    const headingSx = {
        display: 'block', mt: 2, mb: 0.25, px: 1, py: 0.5,
        fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5,
        bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
        borderLeft: 3, borderColor: 'primary.main', borderRadius: 0.5,
    };

    return (
        <Dialog open={!!customer} onClose={saving ? undefined : onClose} {...APP_DIALOG}
            // A fixed height on wider screens too: sized to its content, the window shrank and
            // jumped every time a category chip left only a few products.
            PaperProps={{ sx: { ...APP_DIALOG.PaperProps.sx, height: { xs: '100%', sm: 'calc(100% - 64px)' } } }}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>Change items</Typography>
                    <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.9 }}>
                        {customer?.customer_name}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ color: 'inherit' }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            {/* Search and category chips stay put while the list scrolls under them */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
                <TextField
                    size="small" fullWidth placeholder="Search item"
                    value={search} onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear"><ClearIcon fontSize="small" /></IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                {otherGroups.length > 0 && (
                    <ScrollChipRow px={2} sx={{ mt: 1, mx: -2 }}>
                        <Chip label="All" size="small" clickable
                            color={!catFilter ? 'primary' : 'default'}
                            variant={!catFilter ? 'filled' : 'outlined'}
                            onClick={() => setCatFilter('')} sx={{ flexShrink: 0 }} />
                        {otherGroups.map(({ cat, items }) => {
                            const active = catFilter === cat;
                            return (
                                <Chip key={cat} label={`${cat} (${items.length})`} size="small" clickable
                                    color={active ? 'primary' : 'default'}
                                    variant={active ? 'filled' : 'outlined'}
                                    onClick={() => setCatFilter(active ? '' : cat)}
                                    sx={{ flexShrink: 0 }} />
                            );
                        })}
                    </ScrollChipRow>
                )}
            </Box>

            <DialogContent sx={{ px: 2, pt: 0 }}>

                {shownPlan.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={headingSx}>Customer's items</Typography>
                        {shownPlan.map(({ sub, base, current, isAbsent }) => {
                            const v = valueOf(sub.product_id, current);
                            const note = isAbsent ? 'Absent today'
                                : sub.is_suspended ? 'Paused'
                                : sub.subscribed_qty > 0 ? (v !== base ? `Plan ${base}` : null)
                                : 'Added today';
                            const price = isAbsent ? null : priceLine(sub.price_per_unit || 0, v);
                            return (
                                <Box key={`plan-${sub.product_id}`} sx={rowSx}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>{sub.product_name}</Typography>
                                        {(note || price) && (
                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                {note && (
                                                    <Box component="span" sx={{ color: isAbsent ? 'error.main' : v > base ? 'success.main' : v < base ? 'warning.dark' : 'text.secondary' }}>
                                                        {note}
                                                    </Box>
                                                )}
                                                {note && price && ' · '}
                                                {price}
                                            </Typography>
                                        )}
                                    </Box>
                                    {stepper(sub.product_id, current, { disabled: isAbsent || saving })}
                                </Box>
                            );
                        })}
                    </>
                )}

                {shownGroups.map(({ cat, items }) => (
                    <React.Fragment key={`cat-${cat}`}>
                        <Typography variant="subtitle2" sx={headingSx}>{cat}</Typography>
                        {items.map(p => (
                            <Box key={p.f_id} sx={rowSx}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>{p.f_name}</Typography>
                                    {rateOn(p, date) > 0 && (
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                            {priceLine(rateOn(p, date), qty[p.f_id] || 0)}
                                        </Typography>
                                    )}
                                </Box>
                                {stepper(p.f_id, 0, { showAdd: true, disabled: saving })}
                            </Box>
                        ))}
                    </React.Fragment>
                ))}

                {!shownPlan.length && !shownGroups.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        {q ? 'No item matches your search.' : 'No items to add.'}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>Total</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1.3 }}>{rupees(total)}</Typography>
                </Box>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" color="success" onClick={handleSave}
                    disabled={saving || !changes.length} sx={{ minWidth: 120, height: TOUCH.min }}>
                    {saving ? <CircularProgress size={20} color="inherit" />
                        : changes.length ? `Save (${changes.length})` : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
